import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const AVANTAGES = [
  "Durée de vie exceptionnelle : 50 à 100 ans",
  "Esthétique contemporaine, lignes épurées",
  "Léger : idéal en rénovation sur charpente existante",
  "Quasi aucun entretien, insensible à la mousse",
  "100 % recyclable, matériau durable",
  "S'adapte aux formes de toiture les plus audacieuses",
];

const FAQ = [
  {
    q: "Quelle est la durée de vie d'une couverture zinc joint debout ?",
    a: "Une couverture en zinc à joint debout correctement posée dure entre 50 et 100 ans. Le zinc se patine naturellement et se protège lui-même, ce qui en fait l'un des matériaux de couverture les plus durables du marché.",
  },
  {
    q: "Le zinc joint debout convient-il à une rénovation ?",
    a: "Oui. Le zinc est un matériau léger qui s'adapte très bien aux charpentes existantes, y compris sur des bâtiments anciens. Nous réalisons une étude de votre charpente avant toute réfection pour garantir la compatibilité.",
  },
  {
    q: "Quel entretien pour une toiture zinc ou aluminium ?",
    a: "Quasiment aucun. Contrairement à la tuile ou à l'ardoise, la couverture métallique ne retient ni mousse ni lichen. Une simple inspection visuelle tous les 2 à 3 ans suffit.",
  },
  {
    q: "Zinc ou aluminium : que choisir pour ma toiture ?",
    a: "Les deux offrent d'excellentes performances. Le zinc séduit par sa patine naturelle et sa longévité, l'aluminium par sa légèreté et sa palette de couleurs. Nous vous conseillons selon votre projet, votre budget et le style de votre maison lors de l'étude gratuite.",
  },
];

export default function CouvertureMetallique() {
  return (
    <section id="couverture-metallique" data-testid="zinc-section" className="py-28 lg:py-40 border-t border-white/10 bg-[#0C0C0E]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-jaune font-bold mb-4">La tendance toiture</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl leading-tight">
              Couverture métallique<br />joint debout
            </h2>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed font-light max-w-xl">
              Zinc joint debout ou aluminium : la couverture métallique s'impose comme
              la finition la plus recherchée des toitures contemporaines. En neuf comme
              en rénovation, nous la posons avec la précision qu'exige ce matériau noble,
              partout en Seine-Maritime, dans l'Eure et le Calvados.
            </p>
            <ul className="mt-10 space-y-4" data-testid="zinc-avantages-list">
              {AVANTAGES.map((a, i) => (
                <motion.li
                  key={a}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="flex items-start gap-4 text-sm text-zinc-300"
                >
                  <span className="mt-0.5 w-5 h-5 shrink-0 border border-vert/70 flex items-center justify-center">
                    <Check className="w-3 h-3 text-vert" />
                  </span>
                  {a}
                </motion.li>
              ))}
            </ul>
            <a
              href="#contact"
              data-testid="zinc-cta-devis"
              className="group mt-10 inline-flex items-center gap-2 bg-white text-black px-7 py-4 text-xs tracking-[0.15em] uppercase font-bold hover:bg-jaune transition-colors duration-300"
            >
              Devis couverture métallique
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:sticky lg:top-32"
          >
            <div className="border border-white/10 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1602193230480-7840a38eb0c3?q=80&w=1400&auto=format&fit=crop"
                alt="Toiture en zinc joint debout posée par Goujon Vallée en Normandie"
                loading="lazy"
                className="w-full h-[420px] object-cover"
                data-testid="zinc-image"
              />
            </div>
            <p className="mt-4 text-xs text-zinc-600 italic">Toiture métallique à joint debout — finition contemporaine.</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mt-24 max-w-3xl"
        >
          <div className="flex gap-2 mb-4">
            <span className="h-1 w-8 bg-jaune" />
            <span className="h-1 w-8 bg-vert" />
          </div>
          <h3 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl">
            Questions fréquentes
          </h3>
          <Accordion type="single" collapsible className="mt-8" data-testid="zinc-faq">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`zinc-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base text-zinc-200 hover:text-jaune hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-400 leading-relaxed font-light">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
